import type React from "react";
import { useRef, useEffect, useState, useMemo } from "react";
import { useTabs, type TabType, type Tab } from "@/contexts/tabs-context";
import { useSplitPanel } from "@/contexts/split-panel-context";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
	X,
	Pin,
	MessagesSquare,
	FileText,
	Presentation,
	Video,
	Archive,
	KeyRound,
	Bot,
	FolderKanban,
	ChevronLeft,
	ChevronRight,
	SplitSquareHorizontal,
	SplitSquareVertical,
	FileSpreadsheet,
	Share2,
	Minimize2,
	EllipsisVertical,
	Maximize2,
	Copy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
	useDndContext,
	useDndMonitor,
	useDroppable,
} from "@dnd-kit/core";
import {
	SortableContext,
	horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Channel, Workspace } from "@/utils/custom-types";
import { Button } from "@/components/ui/button";
import { ShareDialog } from "@/components/ui/share-dialog";
import { useCurrentTab } from "@/contexts/current-tab-context";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { toast } from "sonner";

const getTabIcon = (type: TabType) => {
	const icons: Record<TabType, typeof MessagesSquare> = {
		chat: MessagesSquare,
		doc: FileText,
		board: Presentation,
		meet: Video,
		sheet: FileSpreadsheet,
		storage: Archive,
		password: KeyRound,
		automation: Bot,
		project: FolderKanban,
		settings: MessagesSquare,
		more: MessagesSquare,
	};
	return icons[type] || MessagesSquare;
};

// Remove this function - using theme-consistent styling instead

// Remove this function - using theme-consistent styling instead


interface SortableTabItemPreviewProps {
	isActive: boolean;
	isTemporary: boolean;
	isPreview?: boolean;
	tabName: string;
	tabType: TabType
}

const SortableTabItemPreview: React.FC<SortableTabItemPreviewProps> = ({
	isActive,
	isTemporary,
	isPreview = false,
	tabName,
	tabType
}) => {
	const Icon = getTabIcon(tabType);

	if (isPreview) {
		return (
			<div 
				className={cn(
					"group flex h-full min-w-0 cursor-pointer select-none items-center gap-2 border px-3 py-1 transition-all",
					isActive
						? "border-primary/20 bg-secondary font-semibold text-primary shadow-sm"
						: "border-border bg-muted/50 text-card-foreground hover:bg-accent hover:text-accent-foreground",
					isTemporary && "opacity-75",
				)}
			>
				<Icon
					className={cn(
						"h-3.5 w-3.5 flex-shrink-0",
						isActive ? "text-primary" : "text-muted-foreground",
					)}
				/>
				<span
					className={cn(
						"max-w-[120px] truncate text-xs",
						isTemporary && "italic",
					)}
				>
					{tabName}
				</span>
				<button
					className="ml-1 rounded p-0.5 opacity-0 transition-all hover:bg-destructive/20 hover:text-destructive group-hover:opacity-100"
				>
					<X className="h-3 w-3" />
				</button>
			</div>
		)
	}
}

interface SortableTabItemProps {
	tab: Tab;
	displayName: string;
	isActive: boolean;
	isTemporary: boolean;
	onClick: (tabId: string, path: string) => void;
	onDoubleClick: (e: React.MouseEvent, tabId: string) => void;
	onMiddleClick: (e: React.MouseEvent, tabId: string) => void;
	onClose: (e: React.MouseEvent, tabId: string) => void;
	onSplitRight: (tab: Tab) => void;
	onSplitDown: (tab: Tab) => void;
	isDraggingInsideTabBar: boolean;
	preview: boolean;
	onContextMenu?: (e: React.MouseEvent, tab: Tab) => void;
}

const SortableTabItem: React.FC<SortableTabItemProps> = ({
	tab,
	displayName,
	isActive,
	isTemporary,
	onClick,
	onDoubleClick,
	onMiddleClick,
	onClose,
	onSplitRight,
	onSplitDown,
	isDraggingInsideTabBar = false,
	preview,
	onContextMenu
}) => {
	const Icon = getTabIcon(tab.type);

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging: isSortableDragging,
	} = useSortable({ 
		id: tab.id,
		disabled: preview,
	});

	let adjusted = transform;
	if (transform && isSortableDragging && isDraggingInsideTabBar) {
		adjusted = { ...transform, y: 0, scaleX: 1, scaleY: 1 };
	}

	const style = {
		transform: CSS.Transform.toString(adjusted),
		transition: transition || "transform 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
		opacity: isSortableDragging && !isDraggingInsideTabBar ? 0.4 : undefined,
		zIndex: isSortableDragging ? 1000 : undefined,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			onPointerDown={(e) => {
				if (e.button !== 0) return;
				onClick(tab.id, tab.path);
				listeners?.onPointerDown?.(e);
			}}
			onDoubleClick={(e) => onDoubleClick(e, tab.id)}
			onMouseDown={(e) => onMiddleClick(e, tab.id)}
			className={cn(
				"group flex h-full min-w-0 shrink-0 cursor-pointer select-none items-center gap-2 border px-3 py-1 transition-all",
				isActive
					? "border-primary/20 bg-secondary font-semibold text-primary shadow-sm"
					: "border-border bg-muted/50 text-card-foreground hover:bg-accent hover:text-accent-foreground",
				isTemporary && "opacity-75",
			)}
			title={`${displayName}${isTemporary ? " (Preview)" : ""} - Right-click to split`}
			onContextMenu={(e) => {
				e.preventDefault();
				e.stopPropagation();
				onContextMenu?.(e, tab);
			}}
		>
			{tab.isPinned && (
				<Pin
					className={cn(
						"h-3 w-3 flex-shrink-0",
						isActive ? "text-primary" : "text-muted-foreground",
					)}
				/>
			)}
			<Icon
				className={cn(
					"h-3.5 w-3.5 flex-shrink-0",
					isActive ? "text-primary" : "text-muted-foreground",
				)}
			/>
			<span
				className={cn(
					"max-w-[120px] truncate text-xs",
					isTemporary && "italic",
				)}
			>
				{displayName}
			</span>
			{!tab.isPinned && (
				<button
					onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
  					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
					onClick={(e) => {
						e.stopPropagation();
						onClose(e, tab.id);
					}}
					className={cn(
						"ml-1 rounded p-0.5 opacity-0 transition-all hover:bg-destructive/20 hover:text-destructive",
						isActive ? "opacity-100" : "group-hover:opacity-100"
					  )}
				>
					<X className="h-3 w-3" />
				</button>
			)}
		</div>
	);
};

interface TabsSectionProps {
	panelId?: string;
	panelActiveTabId?: string | null;
	onTabClick?: (tabId: string) => void;
	isInsideTabBar: boolean; 
	preview?: boolean;
}

export const TabsSection: React.FC<TabsSectionProps> = ({ 
	panelId = "default",
	panelActiveTabId,
	onTabClick,
	isInsideTabBar,
	preview = false,
}) => {
	const {
		getTabsByPanel,
		activeTabId,
		temporaryTab,
		closeTab,
		closeTabs,
		setActiveTab,
		togglePinTab,
		confirmTab,
		openTab,
		reorderTabs,
		focusedTabId,
		clearFocusedTab,
		setFocusedTab,
	} = useTabs();

	const { channels } = useWorkspaceContext();
	const channelTitleById = useMemo(() => {
		const map = new Map<string, string>();
		for (const channel of channels) {
			map.set(String(channel.id), channel.title ?? `Channel ${channel.id}`);
		}
		return map;
	}, [channels]);
	const getTabDisplayName = (tab: Tab) =>
		channelTitleById.get(tab.channelId) ?? tab.name;
	
	const tabs = getTabsByPanel(panelId || "default");
	const { tabs: allTabs, createTabDataFromChannel } = useTabs(); 
	const { splitPanel, focusedPanelId, focusPanel } = useSplitPanel();
	const isFocusedPanel = panelId === focusedPanelId;
	const navigate = useNavigate();
	const params = useParams({ strict: false });
	const workspaceId = params?.workspaceId || "1";
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [showLeftScroll, setShowLeftScroll] = useState(false);
	const [showRightScroll, setShowRightScroll] = useState(false);

	const [shareDialogOpen, setShareDialogOpen] = useState(false);

	const [contextMenu, setContextMenu] = useState<{
		x: number;
		y: number;
		tab: Tab;
	} | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);

	const handleFocusToggle = () => {
		if (focusedTabId) {
			clearFocusedTab();
		} else {
			focusPanel(panelId);
			setActiveTab(panelActiveTabId as string);
			setFocusedTab(panelActiveTabId as string);
		}
	};

	// Make the tabs section a droppable zone
	const { isOver, setNodeRef: setDroppableRef } = useDroppable({
		id: `tabs-section-${panelId}`,
		data: {
			type: 'tabs-section',
			panelId: panelId,
		},
	});

	// Check scroll buttons visibility
	useEffect(() => {
		const checkScroll = () => {
			const container = scrollContainerRef.current;
			if (!container) return;

			setShowLeftScroll(container.scrollLeft > 0);
			setShowRightScroll(
				container.scrollLeft < container.scrollWidth - container.clientWidth,
			);
		};

		const container = scrollContainerRef.current;
		if (container) {
			container.addEventListener("scroll", checkScroll);
			checkScroll();

			// Check on resize
			const resizeObserver = new ResizeObserver(checkScroll);
			resizeObserver.observe(container);

			return () => {
				container.removeEventListener("scroll", checkScroll);
				resizeObserver.disconnect();
			};
		}
	}, [tabs]);

	const scrollTabs = (direction: "left" | "right") => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const scrollAmount = 200;
		container.scrollBy({
			left: direction === "left" ? -scrollAmount : scrollAmount,
			behavior: "smooth",
		});
	};

	const handleTabClick = (tabId: string, path: string) => {
		// Always handle the tab click, regardless of focus state
		// This ensures clicking a tab in an unfocused panel works immediately
		
		// Update panel-specific active tab
		if (onTabClick) {
			onTabClick(tabId);
		}
		
		// If not focused, focus this panel first
		if (!isFocusedPanel) {
			focusPanel(panelId);
		}
		
		// Always navigate to the clicked tab
		setActiveTab(tabId);
		// console.log("[tabs-section.tsx(handleTabClick)] Calling setActiveTab with ", tabId);

		navigate({ to: path as any });
	};

	const digitRegex = /^\d+$/;

	const getChannelLinkPath = (tab: Tab) => {
		console.log("tab = ", {tab});
		const channel = channels?.find((item) => item.id === Number(tab.channelId));
		console.log("channel = ", {channel});
		const typeSegment = channel?.type ?? tab.type;
		return `/${tab.workspaceId}/${typeSegment}/${tab.channelId}`;
	};

	const handleCopyLink = async (tab: Tab) => {
		if (!digitRegex.test(tab.channelId)) {
			toast("This tab does not have a channel link.");
			return;
		}

		const path = getChannelLinkPath(tab);
		const baseUrl =
			typeof window !== "undefined" ? window.location.origin : "";
		const url = `${baseUrl}${path}`;

		try {
			await navigator.clipboard.writeText(url);
			toast("Channel link copied.");
		} catch (error) {
			console.error("Failed to copy channel link", error);
			toast("Failed to copy channel link.");
		}
	};

	const handleTabContextMenu = (e: React.MouseEvent, tab: Tab) => {
		e.preventDefault();

		const MENU_WIDTH = 160;
  		const MENU_HEIGHT = 200;
		const PADDING = 8; // padding from viewport edges

		let x = e.clientX;
		let y = e.clientY;

		const vw = window.innerWidth;
		const vh = window.innerHeight;

		// Clamp so the menu never goes off the right edge
		if (x + MENU_WIDTH + PADDING > vw) {
			x = vw - MENU_WIDTH - PADDING;
		}
		
		if (y + MENU_HEIGHT + PADDING > vh) {
			y = vh - MENU_HEIGHT - PADDING;
		}
		
		x = Math.max(PADDING, x);
		y = Math.max(PADDING, y);

		setContextMenu({ x, y, tab });
	};

	useEffect(() => {
		if (!contextMenu) return;

		const handleMouseDown = (event: MouseEvent) => {
			if (menuRef.current?.contains(event.target as Node | null)) {
				return;
			}
			setContextMenu(null);
		};

		window.addEventListener("mousedown", handleMouseDown);

		return () => {
			window.removeEventListener("mousedown", handleMouseDown);
		};
	}, [contextMenu]);

	const handleTabDoubleClick = (e: React.MouseEvent, tabId: string) => {
		e.preventDefault();
		e.stopPropagation();

		const tab = tabs.find((t) => t.id === tabId);
		if (tab?.isTemporary) {
			// Convert temporary tab to permanent
			confirmTab(tabId);
		}
		// Remove pin toggle behavior on double-click for permanent tabs
		// Double-click should only be used to confirm temporary tabs
	};

	const handleTabMiddleClick = (e: React.MouseEvent, tabId: string) => {
		if (e.button === 1) {
			e.preventDefault();
			closeTab(tabId);
		}
	};

	const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
		e.stopPropagation();
		closeTab(tabId);
	};

	const handleSplitRight = (tab: Tab) => {
		// Split functionality will be handled by split-panel-context and tabs-context
		splitPanel(panelId, "right", tab);
	};

	const handleSplitDown = (tab: Tab) => {
		// Split functionality will be handled by split-panel-context and tabs-context
		splitPanel(panelId, "down", tab);
	};

	// Sort tabs: pinned first, then by last visited
	const sortedTabs = [...tabs].sort((a, b) => {
		if (a.isPinned && !b.isPinned) return -1;
		if (!a.isPinned && b.isPinned) return 1;
		return 0;
	});

	const { active } = useDndContext();
	const activeId = active?.id ? String(active.id) : null;

	const channel = active?.data?.current?.channel as Channel | undefined;

	const tabData = channel ? createTabDataFromChannel(workspaceId, channel) : null;

	const tabFromChannel = {
		...tabData,
		id: `tab-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
		isTemporary: false,
	};

	const draggedTab = activeId ? allTabs.find((tab) => tab.id === activeId) : undefined;

	const previewTab = isOver
		? (draggedTab ?? tabFromChannel ?? null)
		: null;
	const previewTabName = previewTab ? getTabDisplayName(previewTab) : "";

	const renderTabs = isOver && draggedTab ? 
		sortedTabs.filter((t) => t.id !== draggedTab?.id)
		: sortedTabs;

	const handleShare = () => {
		if (panelActiveTab) {
			setShareDialogOpen(true);
		}
	};

	const panelActiveTab = tabs.find((tab) => tab.id === panelActiveTabId);
	const panelActiveTabName = panelActiveTab
		? getTabDisplayName(panelActiveTab)
		: undefined;

	return (
		<>
			<div 
				className="relative flex h-9 items-center border-border border-b bg-card" 
				data-panel-id={panelId}
				onClick={(e) => e.stopPropagation()}>
				{/* Left scroll button
				{showLeftScroll && (
					<button
						onClick={() => scrollTabs("left")}
						className="absolute left-0 z-10 h-full bg-gradient-to-r from-card to-transparent px-2 transition-all hover:from-accent"
					>
						<ChevronLeft className="h-4 w-4 text-muted-foreground" />
					</button>
				)} */}

				{/* Tabs container */}
				<div
					ref={(node) => {
						scrollContainerRef.current = node;
						setDroppableRef(node);
					}}
					data-tabs-section="true"
					className={cn(
						"scrollbar-none flex flex-1 h-full items-center overflow-x-auto transition-colors",
						// isOver && "bg-primary/10 ring-2 ring-primary/20 ring-inset",
					)}
					style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
				>
					{tabs.length === 0 ? (
						<div className="px-3 text-muted-foreground text-xs">
							Click on items or use the search to open tabs
						</div>
					) : (
						<SortableContext
							items={renderTabs.map(t => t.id)}
							strategy={horizontalListSortingStrategy}
						>
							{renderTabs.map((tab) => (
								<SortableTabItem
									key={tab.id}
									tab={tab}
									displayName={getTabDisplayName(tab)}
									isActive={tab.id === (panelActiveTabId || activeTabId)}
									isTemporary={tab.isTemporary}
									onClick={handleTabClick}
									onDoubleClick={handleTabDoubleClick}
									onMiddleClick={handleTabMiddleClick}
									onClose={handleCloseTab}
									onSplitRight={handleSplitRight}
									onSplitDown={handleSplitDown}
									isDraggingInsideTabBar={isInsideTabBar}
									preview={preview}
									onContextMenu={handleTabContextMenu}
								/>
							))}
							
							{isOver && previewTab && (
								<SortableTabItemPreview
									isActive={previewTab.id === (panelActiveTabId || activeTabId)}
									isTemporary={previewTab.isTemporary}
									isPreview={true}
									tabName={previewTabName}
									tabType={previewTab.type!}
								/>
							)}
						</SortableContext>
					)}
				</div>

				{/* Right scroll button
				{showRightScroll && (
					<button
						onClick={() => scrollTabs("right")}
						className="relative right-0 z-10 h-full bg-gradient-to-l from-card to-transparent px-2 transition-all hover:from-accent"
					>
						<ChevronRight className="h-4 w-4 text-muted-foreground" />
					</button>
				)} */}

				<div className="ml-auto flex flex-row items-center">
					<Button
						variant="ghost"
						size="icon"
						onClick={handleShare}
						title="Share this view"
						className="text-muted-foreground hover:text-foreground"
					>
						<Share2 className="h-4 w-4" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						onClick={() => {
							navigator.clipboard.writeText(window.location.href);
							toast.success("URL copied to clipboard");
						}}
						title="Copy URL"
						className="text-muted-foreground hover:text-foreground"
					>
						<Copy className="h-4 w-4" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						onClick={handleFocusToggle}
						title={focusedTabId ? "Minimize" : "Maximize"}
						className="text-muted-foreground hover:text-foreground"
					>
						{focusedTabId ? (
							<Minimize2 className="h-4 w-4" />
						) : (
							<Maximize2 className="h-4 w-4" />
						)}
					</Button>
					{/* <Button
						variant="ghost"
						size="icon"
						// onClick={handleShare}
						title="Share this view"
						className="text-muted-foreground hover:text-foreground"
					>
						<EllipsisVertical className="h-4 w-4" />
					</Button> */}
				</div>
				<ShareDialog
					open={shareDialogOpen}
					onOpenChange={setShareDialogOpen}
					workspaceId={workspaceId}
					channelId={panelActiveTab?.channelId}
					channelTitle={panelActiveTabName}
				/>

				{contextMenu && (
					<div
						className="fixed z-50 w-40 rounded-md border bg-popover text-popover-foreground shadow-lg py-1"
						style={{ top: contextMenu.y, left: contextMenu.x }}
						ref={menuRef}
						onClick={(e) => e.stopPropagation()}
					>
						<button
							type="button"
							className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
							onClick={() => {
								closeTab(contextMenu.tab.id)
								setContextMenu(null);
							}}
						>
							Close
						</button>
						<button
							type="button"
							className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
							onClick={() => {
								closeTabs(tabs)
								setContextMenu(null);
							}}
						>
							Close All
						</button>
						{/* {digitRegex.test(contextMenu.tab.channelId) && (
							<button
								type="button"
								className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
								onClick={() => {
									handleCopyLink(contextMenu.tab);
									setContextMenu(null);
								}}
							>
								Copy Link
							</button>
						)} */}
					</div>
				)}
			</div>
		</>
	);
};
